import { LanguagesEnum } from '@/api/enums';
import { Slide, TextAlignment } from '@/client/courses/types/courses.type';
import { useAccountVariables } from '@/hooks/query/account-variables.hooks';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import {
  RichTextEditor,
  RichTextEditorHandle,
  RichTextVendorEditor,
  setupTooltipEditOnHover,
} from '@/ui/rich-text';
import { isRtlLanguage } from '@/utils/helpers';
import {
  POINT_OF_CONTACT_FORMAT,
  withExamplePointOfContact,
} from '@/utils/helpers/point-of-contact.helper';
import { PostHogFeatureFlags } from '@/utils/posthog/posthog.events';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export const SLIDE_FORMATS = [
  'bold',
  'italic',
  'underline',
  'list',
  'bullet',
  'indent',
  'link',
  'color',
  'background',
  'tooltip',
  'image',
  'width',
  'height',
  POINT_OF_CONTACT_FORMAT,
];

type RichTextAreaProps = {
  currentEditingSlide: Slide;
  isEditingPopUpSlide: boolean;
  selectedLanguage: string;
  currentSlideIdx: number;
  currentSlideData: Slide;
  updateSlide: (id: string, updates: Partial<Slide>) => void;
  isPurpleBackground?: boolean;
  registerEditor: (handle: RichTextEditorHandle | null) => void;
  vendorEditorRef: React.RefObject<RichTextVendorEditor>;
};

export const RichTextArea: React.FC<RichTextAreaProps> = ({
  currentEditingSlide,
  isEditingPopUpSlide,
  selectedLanguage,
  currentSlideIdx,
  currentSlideData,
  updateSlide,
  isPurpleBackground,
  registerEditor,
  vendorEditorRef,
}) => {
  const { t } = useTranslation();
  const editorRef = useRef<RichTextEditorHandle>(null);
  const toast = useToast();

  // The toolbar drives this editor whether or not it has been focused yet
  useEffect(() => {
    registerEditor(editorRef.current);
  }, [
    registerEditor,
    currentSlideIdx,
    selectedLanguage,
    currentEditingSlide?.id,
  ]);

  const currentAccount = useAppSelector(selectCurrentAccount);
  const { canUseFeature } = useFeatureFlag();
  const showPointsOfContact = canUseFeature(
    PostHogFeatureFlags.TRAINING_VARIABLES,
  );
  const { variables } = useAccountVariables(currentAccount?.id, {
    enabled: showPointsOfContact,
  });

  useEffect(() => {
    const teardown = setupTooltipEditOnHover(
      vendorEditorRef,
      currentAccount?.isSystem
        ? withExamplePointOfContact(variables, t)
        : variables,
      () => toast?.success('', t('clipboard.copied')),
      isRtlLanguage(selectedLanguage as LanguagesEnum),
    );

    return () => teardown?.();
  }, [
    vendorEditorRef,
    currentSlideIdx,
    selectedLanguage,
    currentEditingSlide?.id,
    currentAccount?.isSystem,
    variables,
    t,
    toast,
  ]);

  return (
    <RichTextEditor
      ref={editorRef}
      key={`${selectedLanguage}-${currentEditingSlide?.id}-${currentSlideIdx}-${
        isEditingPopUpSlide ? 'popup' : 'main'
      }`}
      value={currentEditingSlide?.html || ''}
      theme="snow"
      formats={SLIDE_FORMATS}
      changeSources={['user', 'api']}
      registerColorFormat
      stripPasteFormatting
      className={`rich-text-editor rich-text-editor-v2 ${
        currentSlideData.textAlignment === TextAlignment.CENTER
          ? 'text-center'
          : 'text-left'
      } ${isPurpleBackground ? 'rich-text-editor-summary' : ''}`}
      placeholder={
        isEditingPopUpSlide
          ? t('content.popup.slide.content')
          : t('content.slide.content')
      }
      onFocus={() => registerEditor(editorRef.current)}
      onChange={(html, { text }) => {
        if (!currentEditingSlide?.id) return;

        const hasContent = text.trim().length > 0 || /<img\b/i.test(html);
        const newHtml = hasContent ? html : '';
        if (newHtml === (currentEditingSlide?.html ?? '')) return;

        updateSlide(currentEditingSlide.id, { html: newHtml });
      }}
    />
  );
};
